import axios from "@/plugins/axios";
import {
    canSearchGlobalType,
    getAllowedGlobalSearchTypes,
    DEFAULT_GLOBAL_SEARCH_TYPES,
} from "@/utils/globalSearchPermissions";

const SEARCH_ENDPOINT = "/api/search/";

const SEARCH_TYPES = [
    "providers",
    "customers",
    "contacts",
    "shipments",
    "receipts",
    "establishments",
    "bundles",
    "products",
    "providerproducts",
    "customerorders",
    "providerorders",
    "customerinvoices",
    "providerinvoices",
    "stocks",
    "projects",
    "tasks",
    "calendar_events",
];

const INITIAL_LIMIT = 3;
const LOAD_MORE_LIMIT = 20;

function buildTypeMap(createValue) {
    return SEARCH_TYPES.reduce((map, type) => {
        map[type] = createValue();
        return map;
    }, {});
}

function extractTypeResults(payload, type) {
    const group = payload && payload.results && payload.results[type];
    if (!group || typeof group !== "object") {
        return { results: [], count: 0, hasMore: false };
    }
    const results = Array.isArray(group.results) ? group.results : [];
    const hasMore =
        group.has_more !== undefined
            ? Boolean(group.has_more)
            : group.count != null && group.count > results.length;
    const count =
        group.count !== undefined && group.count !== null
            ? group.count
            : hasMore
              ? null
              : results.length;
    return { results, count, hasMore };
}

function normalizeSearchPayload(payload) {
    if (payload && typeof payload === "object" && !Array.isArray(payload)) {
        return {
            query: payload.query,
            types: payload.types || null,
            limit: payload.limit === LOAD_MORE_LIMIT ? LOAD_MORE_LIMIT : INITIAL_LIMIT,
        };
    }
    return { query: payload, types: null, limit: INITIAL_LIMIT };
}

function applyTypePage(commit, type, { results, count, hasMore }) {
    commit("updateResults", { type, results });
    commit("setCount", { type, count });
    commit("setHasMore", {
        type,
        hasMore: hasMore === true,
    });
}

const state = {
    query: "",
    // Incremented on every search: identifies the batch a response belongs to.
    requestId: 0,
    results: buildTypeMap(() => []),
    expanded: buildTypeMap(() => false),
    hasMore: buildTypeMap(() => true),
    counts: buildTypeMap(() => null),
    loading: false,
    loadingTypes: buildTypeMap(() => false),
    loadingMore: buildTypeMap(() => false),
    error: null,
    cancelTokenSource: null,
    scrollTop: 0,
    limits: buildTypeMap(() => INITIAL_LIMIT),
};

const mutations = {
    updateQuery(state, query) {
        state.query = query;
    },
    updateRequestId(state, requestId) {
        state.requestId = requestId;
    },
    updateResults(state, { type, results }) {
        state.results[type] = results;
    },
    appendResults(state, { type, results }) {
        state.results[type] = [...(state.results[type] || []), ...results];
    },
    clearResults(state) {
        state.results = buildTypeMap(() => []);
    },
    clearExpanded(state) {
        state.expanded = buildTypeMap(() => false);
    },
    clearHasMore(state) {
        state.hasMore = buildTypeMap(() => true);
    },
    setCount(state, { type, count }) {
        state.counts[type] = count == null ? null : count;
    },
    clearCounts(state) {
        state.counts = buildTypeMap(() => null);
    },
    updateLoading(state, loading) {
        state.loading = loading;
    },
    setTypeLoading(state, { type, loading }) {
        state.loadingTypes[type] = loading;
    },
    setTypesLoading(state, { types, loading }) {
        (types || []).forEach((type) => {
            state.loadingTypes[type] = loading;
        });
    },
    clearLoadingTypes(state) {
        state.loadingTypes = buildTypeMap(() => false);
    },
    updateLoadingMore(state, { type, loading }) {
        state.loadingMore[type] = loading;
    },
    clearLoadingMore(state) {
        state.loadingMore = buildTypeMap(() => false);
    },
    updateError(state, error) {
        state.error = error;
    },
    toggleExpanded(state, type) {
        state.expanded[type] = !state.expanded[type];
    },
    setExpanded(state, { type, expanded }) {
        state.expanded[type] = expanded;
    },
    setHasMore(state, { type, hasMore }) {
        state.hasMore[type] = hasMore;
    },
    updateCancelTokenSource(state, source) {
        state.cancelTokenSource = source;
    },
    saveScrollPosition(state, scrollTop) {
        state.scrollTop = scrollTop || 0;
    },
};

const actions = {
    async search({ commit, state }, payload) {
        const { query, types, limit } = normalizeSearchPayload(payload);

        // Cancel any pending requests
        if (state.cancelTokenSource) {
            state.cancelTokenSource.cancel("New search started");
        }

        const requestId = state.requestId + 1;
        commit("updateRequestId", requestId);
        commit("clearResults");
        commit("clearExpanded");
        commit("clearHasMore");
        commit("clearCounts");
        commit("clearLoadingTypes");
        commit("clearLoadingMore");
        commit("updateQuery", query || "");

        const trimmedQuery = String(query || "").trim();
        if (!trimmedQuery) {
            commit("updateLoading", false);
            commit("updateCancelTokenSource", null);
            return;
        }

        const requestedTypes = types || DEFAULT_GLOBAL_SEARCH_TYPES;
        const typesToSearch = getAllowedGlobalSearchTypes(requestedTypes).filter(
            (type) => SEARCH_TYPES.includes(type),
        );

        if (typesToSearch.length === 0) {
            commit("updateLoading", false);
            commit("updateCancelTokenSource", null);
            return;
        }

        const source = axios.CancelToken.source();
        commit("updateCancelTokenSource", source);
        commit("saveScrollPosition", 0);
        commit("updateError", null);
        commit("setTypesLoading", { types: typesToSearch, loading: true });
        commit("updateLoading", true);

        const isCurrentRequest = () => state.requestId === requestId;

        try {
            const response = await axios.get(SEARCH_ENDPOINT, {
                params: {
                    q: trimmedQuery,
                    limit,
                    types: typesToSearch.join(","),
                },
                cancelToken: source.token,
            });
            if (!isCurrentRequest()) {
                return;
            }
            typesToSearch.forEach((type) => {
                applyTypePage(commit, type, extractTypeResults(response.data, type));
                commit("setTypeLoading", { type, loading: false });
            });
        } catch (err) {
            if (axios.isCancel(err) || !isCurrentRequest()) {
                return;
            }
            commit("updateError", err);
            typesToSearch.forEach((type) => {
                applyTypePage(commit, type, {
                    results: [],
                    count: 0,
                    hasMore: false,
                });
                commit("setTypeLoading", { type, loading: false });
            });
        } finally {
            if (state.cancelTokenSource === source) {
                commit("updateLoading", false);
                commit("updateCancelTokenSource", null);
            }
        }
    },

    async toggleExpanded({ commit, dispatch, state }, type) {
        const hasMore = state.hasMore[type] === true;
        const currentResults = state.results[type] || [];
        const currentCount = currentResults.length;
        const isExpanded = state.expanded[type];

        if (hasMore) {
            await dispatch("loadMoreResults", type);
            if (!isExpanded) {
                commit("toggleExpanded", type);
            }
        } else if (isExpanded && currentCount > INITIAL_LIMIT) {
            const firstThree = currentResults.slice(0, INITIAL_LIMIT);
            commit("updateResults", { type, results: firstThree });
            commit("toggleExpanded", type);
        }
    },
    async loadMoreResults({ commit, state }, type) {
        if (!canSearchGlobalType(type)) {
            return;
        }
        if (!SEARCH_TYPES.includes(type)) {
            return;
        }
        const trimmedQuery = String(state.query || "").trim();
        if (!trimmedQuery) {
            return;
        }

        const requestId = state.requestId;

        commit("updateLoadingMore", { type, loading: true });

        const currentResults = state.results[type] || [];
        const currentCount = currentResults.length;

        // First "load more" click: refetch with limit=20 (no offset, as offsets
        // work with multiples), which replaces the initial 3 results.
        const shouldReplaceResults = currentCount === INITIAL_LIMIT;
        const searchParams = {
            q: trimmedQuery,
            types: type,
            limit: LOAD_MORE_LIMIT,
        };
        if (!shouldReplaceResults) {
            searchParams.offset = currentCount;
        }

        try {
            const response = await axios.get(SEARCH_ENDPOINT, { params: searchParams });
            if (state.requestId !== requestId) {
                return;
            }
            const { results, count, hasMore } = extractTypeResults(response.data, type);

            if (shouldReplaceResults) {
                commit("updateResults", { type, results });
            } else {
                commit("appendResults", { type, results });
            }

            commit("setCount", { type, count });
            commit("setHasMore", { type, hasMore: hasMore === true });
        } catch (err) {
            if (!axios.isCancel(err)) {
                console.error(`Error loading more results for ${type}:`, err);
            }
        } finally {
            if (state.requestId === requestId) {
                commit("updateLoadingMore", { type, loading: false });
            }
        }
    },

    async searchByType({ dispatch }, { query, type }) {
        await dispatch("search", { query, types: [type] });
    },
};

const getters = {
    hasResults(state) {
        return SEARCH_TYPES.some(
            (type) => (state.results[type] || []).length > 0,
        );
    },
    getResultsForType: (state) => (type) => {
        const results = state.results[type] || [];
        const limit = state.limits[type] || 5;
        const expanded = state.expanded[type] || false;

        if (expanded) {
            return results;
        }
        return results.slice(0, limit);
    },
    hasMoreResults: (state) => (type) => {
        const results = state.results[type] || [];
        const limit = state.limits[type] || 5;
        return results.length > limit;
    },
    totalResultsCount(state) {
        return SEARCH_TYPES.reduce(
            (total, type) => total + (state.results[type] || []).length,
            0,
        );
    },
    isTypeLoading: (state) => (type) => state.loadingTypes[type] === true,
    pendingTypes(state) {
        return SEARCH_TYPES.filter((type) => state.loadingTypes[type] === true);
    },
    isQueryFetched: (state) => (query) => {
        const trimmed = String(query || "").trim();
        if (!trimmed) {
            return false;
        }
        return String(state.query || "").trim() === trimmed && !state.loading;
    },
    isQueryPending: (state) => (query) => {
        const trimmed = String(query || "").trim();
        if (!trimmed) {
            return false;
        }
        return String(state.query || "").trim() === trimmed && state.loading;
    },
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
    getters,
};
